import React, { useEffect, useState, useRef } from 'react';
import { Upload, X, FileText, FileSpreadsheet, AlertCircle, Plus, CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { computeFileHash } from '@/lib/fileHash';
import { useDuplicateFileCheck, DuplicateMatch } from '@/hooks/useDuplicateFileCheck';

const ALLOWED_EXTENSIONS = ['pdf'];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export interface SelectedFileData {
  file: File;
  id: string;
  manufacturerName: string;
  mpoNumber: string;
  purchaseDate: Date;
  fileHash?: string;   // undefined = still computing; '' = hash failed; string = hex digest
  duplicateWarningDismissed?: boolean;
}

export interface ExistingFileData {
  id: string;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  storage_path: string;
  mpo_number: string | null;
}

interface POMultiFileUploadProps {
  onFilesChanged: (files: SelectedFileData[]) => void;
  existingFiles?: ExistingFileData[];
  onExistingFileRemoved?: (fileId: string) => void;
  onExistingFileMpoNumberChange?: (fileId: string, mpoNumber: string) => void;
  disabled?: boolean;
  /**
   * When true, the component shows existing files but removes any controls
   * for adding new upload files.
   */
  disableNewUploads?: boolean;
  manufacturers: string[];
  organisationId: string;
  onManufacturerAdded?: (name: string) => void;
}

const getFileIcon = (fileName: string | null | undefined) => {
  if (!fileName) return <FileText className="h-5 w-5 text-muted-foreground" />;
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return <FileText className="h-5 w-5 text-destructive" />;
  if (['xls', 'xlsx', 'csv'].includes(ext || '')) return <FileSpreadsheet className="h-5 w-5 text-primary" />;
  return <FileText className="h-5 w-5 text-muted-foreground" />;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const validateFile = (file: File): { valid: boolean; error?: string } => {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';

  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `${file.name}: Unsupported file type. Please upload a PDF file.`
    };
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      valid: false,
      error: `${file.name}: File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`
    };
  }

  return { valid: true };
};

const POMultiFileUpload: React.FC<POMultiFileUploadProps> = ({
  onFilesChanged,
  existingFiles = [],
  onExistingFileRemoved,
  onExistingFileMpoNumberChange,
  disabled = false,
  disableNewUploads = false,
  manufacturers,
  organisationId,
  onManufacturerAdded,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFileData[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [showNewManufacturerInput, setShowNewManufacturerInput] = useState<string | null>(null);
  const [newManufacturerName, setNewManufacturerName] = useState('');
  const [savingManufacturer, setSavingManufacturer] = useState(false);

  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const newUploadsDisabled = disabled || disableNewUploads;

  const fileHashMap: Record<string, string> = Object.fromEntries(
    selectedFiles
      .filter(f => f.fileHash)
      .map(f => [f.id, f.fileHash!])
  );
  const duplicateMatches = useDuplicateFileCheck(organisationId, fileHashMap);

  useEffect(() => {
    if (!disableNewUploads) return;
    // Ensure edit-mode never keeps stale pending uploads.
    setSelectedFiles([]);
    setFileErrors([]);
    setIsDragging(false);
    setShowNewManufacturerInput(null);
    setNewManufacturerName('');
  }, [disableNewUploads]);

  const notifyParent = (files: SelectedFileData[]) => {
    onFilesChanged(files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!newUploadsDisabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (newUploadsDisabled) return;

    const files = Array.from(e.dataTransfer.files);
    handleFilesSelected(files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    handleFilesSelected(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFilesSelected = (files: File[]) => {
    setFileErrors([]);
    const errors: string[] = [];
    const validFiles: SelectedFileData[] = [];

    for (const file of files) {
      const validation = validateFile(file);
      if (!validation.valid) {
        errors.push(validation.error || 'Invalid file');
      } else {
        validFiles.push({
          file,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          manufacturerName: '',
          mpoNumber: '',
          purchaseDate: new Date(),
        });
      }
    }

    if (errors.length > 0) {
      setFileErrors(errors);
    }

    if (validFiles.length > 0) {
      const updatedFiles = [...selectedFiles, ...validFiles];
      setSelectedFiles(updatedFiles);
      notifyParent(updatedFiles);

      for (const fileData of validFiles) {
        computeFileHash(fileData.file)
          .then(hash => {
            if (!isMountedRef.current) return;
            setSelectedFiles(prev => {
              const next = prev.map(f => f.id === fileData.id ? { ...f, fileHash: hash } : f);
              notifyParent(next);
              return next;
            });
          })
          .catch(() => {
            if (!isMountedRef.current) return;
            setSelectedFiles(prev => {
              const next = prev.map(f => f.id === fileData.id ? { ...f, fileHash: '' } : f);
              notifyParent(next);
              return next;
            });
          });
      }
    }
  };

  const handleRemoveFile = (fileId: string) => {
    const updatedFiles = selectedFiles.filter(f => f.id !== fileId);
    setSelectedFiles(updatedFiles);
    notifyParent(updatedFiles);
  };

  const handleDismissDuplicateWarning = (fileId: string) => {
    const updated = selectedFiles.map(f =>
      f.id === fileId ? { ...f, duplicateWarningDismissed: true } : f
    );
    setSelectedFiles(updated);
    notifyParent(updated);
  };

  const handleManufacturerChange = (fileId: string, manufacturerName: string) => {
    if (manufacturerName === '__add_new__') {
      setShowNewManufacturerInput(fileId);
      setNewManufacturerName('');
      return;
    }

    const updatedFiles = selectedFiles.map(f =>
      f.id === fileId ? { ...f, manufacturerName } : f
    );
    setSelectedFiles(updatedFiles);
    notifyParent(updatedFiles);
  };

  const handleDateChange = (fileId: string, date: Date | undefined) => {
    if (!date) return;
    const updatedFiles = selectedFiles.map(f =>
      f.id === fileId ? { ...f, purchaseDate: date } : f
    );
    setSelectedFiles(updatedFiles);
    notifyParent(updatedFiles);
  };

  const handleMpoNumberChange = (fileId: string, mpoNumber: string) => {
    const updatedFiles = selectedFiles.map(f =>
      f.id === fileId ? { ...f, mpoNumber } : f
    );
    setSelectedFiles(updatedFiles);
    notifyParent(updatedFiles);
  };

  const handleAddNewManufacturer = async (fileId: string) => {
    if (!newManufacturerName.trim()) return;

    setSavingManufacturer(true);
    try {
      const { error } = await supabase
        .from('manufacturers')
        .insert({ name: newManufacturerName.trim(), organisation_id: organisationId });

      if (error && !error.message.includes('duplicate')) {
        throw error;
      }

      const updatedFiles = selectedFiles.map(f =>
        f.id === fileId ? { ...f, manufacturerName: newManufacturerName.trim() } : f
      );
      setSelectedFiles(updatedFiles);
      notifyParent(updatedFiles);

      onManufacturerAdded?.(newManufacturerName.trim());

      setShowNewManufacturerInput(null);
      setNewManufacturerName('');
    } catch (error) {
      console.error('Error adding manufacturer:', error);
    } finally {
      setSavingManufacturer(false);
    }
  };

  const handleSelectFileClick = () => {
    if (newUploadsDisabled) return;
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4" data-testid="po-multi-file-upload">
      {/* Existing Files Display */}
      {existingFiles.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Current Files</Label>
          <div className="space-y-2">
            {existingFiles.map((file) => (
              <div key={file.id} className="p-3 border rounded-lg space-y-3" data-testid="existing-file-display">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getFileIcon(file.file_name)}
                    <div>
                      <p className="font-medium text-sm">{file.file_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(file.file_size || 0)}
                      </p>
                    </div>
                  </div>
                  {onExistingFileRemoved && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onExistingFileRemoved(file.id)}
                      disabled={disabled}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {onExistingFileMpoNumberChange && (
                  <div className="space-y-1">
                    <Label htmlFor={`existing-mpo-${file.id}`} className="text-xs text-muted-foreground">
                      mPO Number *
                    </Label>
                    <Input
                      id={`existing-mpo-${file.id}`}
                      placeholder="Enter mPO number *"
                      value={file.mpo_number ?? ''}
                      onChange={(e) => onExistingFileMpoNumberChange(file.id, e.target.value)}
                      disabled={disabled}
                      data-testid={`existing-mpo-input-${file.id}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Files List with Per-File Manufacturer and Date */}
      {!disableNewUploads && selectedFiles.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm">Files to Upload</Label>
          <div className="space-y-3">
            {selectedFiles.map((fileData) => {
              const duplicateMatch = (fileData.fileHash !== undefined && !fileData.duplicateWarningDismissed)
                ? duplicateMatches.get(fileData.id) ?? null
                : null;
              return (
              <div key={fileData.id} className="p-3 border rounded-lg space-y-3">
                {/* File header row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getFileIcon(fileData.file.name)}
                    <div>
                      <p className="font-medium text-sm truncate max-w-[180px]">{fileData.file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(fileData.file.size)}</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveFile(fileData.id)}
                    disabled={disabled}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Hashing indicator */}
                {fileData.fileHash === undefined && (
                  <p className="text-xs text-muted-foreground animate-pulse">Verifying file...</p>
                )}

                {/* Duplicate warning */}
                {duplicateMatch && (
                  <div className="flex items-start gap-2 rounded border border-yellow-300 bg-yellow-50 p-2 text-sm text-yellow-800">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span className="flex-1">
                      Matches <strong>{duplicateMatch.file_name}</strong> uploaded on{' '}
                      {format(new Date(duplicateMatch.uploaded_at), 'PP')}. Upload anyway?
                    </span>
                    <div className="ml-1 border-l border-yellow-300 pl-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-yellow-900 hover:bg-yellow-700 hover:text-white"
                        onClick={() => handleDismissDuplicateWarning(fileData.id)}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-1">
                  <Label htmlFor={`mpo-${fileData.id}`} className="text-xs text-muted-foreground">
                    mPO Number *
                  </Label>
                  <Input
                    id={`mpo-${fileData.id}`}
                    placeholder="Enter mPO number *"
                    value={fileData.mpoNumber}
                    onChange={(e) => handleMpoNumberChange(fileData.id, e.target.value)}
                    disabled={disabled}
                    data-testid={`mpo-input-${fileData.id}`}
                  />
                </div>

                {/* Per-file manufacturer dropdown */}
                {showNewManufacturerInput === fileData.id ? (
                  <div className="flex gap-2">
                    <Input
                      placeholder="New manufacturer name"
                      value={newManufacturerName}
                      onChange={(e) => setNewManufacturerName(e.target.value)}
                      disabled={savingManufacturer}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleAddNewManufacturer(fileData.id)}
                      disabled={savingManufacturer || !newManufacturerName.trim()}
                    >
                      Add
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowNewManufacturerInput(null)}
                      disabled={savingManufacturer}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Select
                    value={fileData.manufacturerName}
                    onValueChange={(value) => handleManufacturerChange(fileData.id, value)}
                    disabled={disabled}
                  >
                    <SelectTrigger className={cn(
                      "w-full",
                      !fileData.manufacturerName && "text-muted-foreground"
                    )}>
                      <SelectValue placeholder="Select manufacturer *" />
                    </SelectTrigger>
                    <SelectContent>
                      {manufacturers.map((name) => (
                        <SelectItem key={name} value={name}>
                          {name}
                        </SelectItem>
                      ))}
                      <SelectItem value="__add_new__">
                        <span className="flex items-center gap-2 text-primary">
                          <Plus className="h-4 w-4" />
                          Add new manufacturer
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}

                {/* Per-file date picker */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Purchase Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !fileData.purchaseDate && "text-muted-foreground"
                        )}
                        disabled={disabled}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {fileData.purchaseDate ? format(fileData.purchaseDate, "PPP") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={fileData.purchaseDate}
                        onSelect={(date) => handleDateChange(fileData.id, date)}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Drop Zone */}
      {!disableNewUploads && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'border-2 border-dashed rounded-lg p-6 transition-colors text-center',
            isDragging && 'border-primary bg-primary/5',
            fileErrors.length > 0 && 'border-destructive bg-destructive/5',
            newUploadsDisabled && 'opacity-50 cursor-not-allowed',
            !isDragging && fileErrors.length === 0 && 'border-muted-foreground/25 hover:border-muted-foreground/50',
          )}
          data-testid="drop-zone"
        >
          <div className="space-y-3">
            <Upload
              className={cn('h-10 w-10 mx-auto', isDragging ? 'text-primary' : 'text-muted-foreground')}
            />
            <div>
              <p className="font-medium">
                {isDragging ? 'Drop files here' : 'Drag and drop files here'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                or click the button below to select files
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              Supported format: PDF • Max size: {MAX_FILE_SIZE_MB}MB per file
            </p>
          </div>
        </div>
      )}

      {/* File Errors Display */}
      {!disableNewUploads && fileErrors.length > 0 && (
        <div className="space-y-1" data-testid="file-errors">
          {fileErrors.map((error, index) => (
            <div key={index} className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          ))}
        </div>
      )}

      {/* Hidden File Input */}
      {!disableNewUploads && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          multiple
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
          data-testid="file-input"
        />
      )}

      {/* Select Files Button */}
      {!disableNewUploads && (
        <Button
          type="button"
          variant="outline"
          onClick={handleSelectFileClick}
          disabled={disabled}
          className="w-full"
          data-testid="select-files-button"
        >
          <Upload className="h-4 w-4 mr-2" />
          {selectedFiles.length > 0 ? 'Add More Files' : 'Select Files'}
        </Button>
      )}

      {/* Info message about per-file PO */}
      {!disableNewUploads && selectedFiles.length > 0 && (
        <div className="text-sm p-3 rounded-lg bg-muted border">
          <p className="font-medium">
            {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} ready
          </p>
          <p className="text-muted-foreground text-xs mt-1">
            Each file will create a separate Purchase Order
          </p>
        </div>
      )}
    </div>
  );
};

export default POMultiFileUpload;
